// Inbox role model — the perspective-specific tab SETS (Design Bible D1/D20/D23)
// and the viewer perspective used by laneMapping.
//
// D20 ROLE-INBOX MATRIX (verbatim intent):
//   Agent / AgencyAdmin → DM · Last Minute · Agent Req. · Club Req. · Scout Req.
//   Club (ClubAdmin/Coach/Staff-with-club) → DM · Last Minute · Club Req. ·
//       Trusted Agent · Scout Req.   (action visibility per ClubRole permissions)
//   Lawyer (pure) / Player / Organiser → DM only, NO tab bar
//   Staff WITHOUT club → DM · Club Req. (job board), NO Agent Req.
//   Dual roles → the UNION of their lanes.
//
// The platform has a role switcher (auth.currentRole) but the Bible binds the
// inbox to the UNION of a user's working roles, so this reads auth.roles — a
// role-upgrade "reveals" its inbox lanes (D20 organiser/staff notes) without a
// switch. DM-only roles contribute NO lanes, so a dual Agent&Lawyer naturally
// resolves to the agent inbox (union of agent lanes + nothing).

import { ROLES } from "../config/roles";
import { LANE, PERSPECTIVE } from "./laneMapping";

// Canonical lane order for the union (Agent set then the club-only extras), so
// every role's tabs come out in the Bible's left-to-right order:
//   Agent → dm,last_minute,agent_req,club_req,scout_req
//   Club  → dm,last_minute,club_req,trusted_agent,scout_req
export const LANE_ORDER = [
  LANE.DM,
  LANE.LAST_MINUTE,
  LANE.AGENT_REQ,
  LANE.CLUB_REQ,
  LANE.TRUSTED_AGENT,
  LANE.SCOUT_REQ,
];

export const LANE_LABELS = {
  [LANE.DM]: "DM",
  [LANE.LAST_MINUTE]: "Last Minute",
  [LANE.AGENT_REQ]: "Agent Req.",
  [LANE.CLUB_REQ]: "Club Req.",
  [LANE.TRUSTED_AGENT]: "Trusted Agent",
  [LANE.SCOUT_REQ]: "Scout Req.",
};

const AGENT_LANES = [
  LANE.DM,
  LANE.LAST_MINUTE,
  LANE.AGENT_REQ,
  LANE.CLUB_REQ,
  LANE.SCOUT_REQ,
];
const CLUB_LANES = [
  LANE.DM,
  LANE.LAST_MINUTE,
  LANE.CLUB_REQ,
  LANE.TRUSTED_AGENT,
  LANE.SCOUT_REQ,
];
// Staff without a club: the job-board case (D20/D23) — DM + Club Req only.
const STAFF_NO_CLUB_LANES = [LANE.DM, LANE.CLUB_REQ];

const isAgentRole = (r) => r === ROLES.Agent || r === ROLES.AgencyAdmin;
const isClubRole = (r) =>
  r === ROLES.ClubAdmin || r === ROLES.Coach; // Staff handled separately (needs hasClub)

// Per-role lane contribution. Staff depends on hasClub; DM-only roles contribute
// nothing (the inbox shows a bare DM list with no tab bar when the union empties).
const laneContribution = (role, { hasClub }) => {
  if (isAgentRole(role)) return AGENT_LANES;
  if (isClubRole(role)) return CLUB_LANES;
  if (role === ROLES.Staff) return hasClub ? CLUB_LANES : STAFF_NO_CLUB_LANES;
  // Lawyer / Player / Organizer → DM only, no lanes.
  return [];
};

// hasClubMembership — Staff-with-club vs job-seeker. The channel user only
// carries club_name today; club_id / club_account_id are honored if present.
export const hasClubMembership = (user) =>
  Boolean(user?.club_name || user?.club_id || user?.club_account_id);

// computeInboxModel — the single source the inbox shell reads. Returns the
// ordered tab list (canonical keys + labels), whether to render the tab bar at
// all (DM-only roles hide it), the default active lane, and the capability flags
// laneMapping needs for viewer-relative filing.
export const computeInboxModel = ({ roles = [], user = null } = {}) => {
  const roleList = Array.isArray(roles) ? roles : [];
  const hasClub = hasClubMembership(user);

  const laneSet = new Set();
  for (const role of roleList) {
    for (const lane of laneContribution(role, { hasClub })) laneSet.add(lane);
  }

  const agentCapable = roleList.some(isAgentRole);
  const clubCapable =
    roleList.some(isClubRole) || (roleList.includes(ROLES.Staff) && hasClub);

  // Order the union canonically and build labelled tabs.
  const laneKeys = LANE_ORDER.filter((k) => laneSet.has(k));
  const tabs = laneKeys.map((key) => ({ key, label: LANE_LABELS[key] }));

  // DM-only (no working lanes, or the degenerate DM-only union): no tab bar.
  const showTabBar = tabs.length > 1;
  const defaultLane = laneKeys[0] || LANE.DM;

  return {
    tabs, // [{ key, label }]
    laneKeys, // ["dm", ...]
    showTabBar,
    defaultLane,
    agentCapable,
    clubCapable,
    hasClub,
  };
};

// channelPerspective — which side (agent/club) the viewer is on for a given
// channel, used by laneMapping.resolveLane for the two viewer-relative origins.
//   • single-capability users resolve exactly (agent-only → agent, club-only →
//     club);
//   • dual agent&club users are inferred from the viewer's own member record on
//     the channel (agency → agent, club → club), defaulting to agent.
export const channelPerspective = (
  channelData,
  { agentCapable, clubCapable },
  userId,
) => {
  if (agentCapable && !clubCapable) return PERSPECTIVE.AGENT;
  if (clubCapable && !agentCapable) return PERSPECTIVE.CLUB;

  // Dual (or unknown): read the viewer's own stamp on the channel.
  const users = Array.isArray(channelData?.users) ? channelData.users : [];
  const self = users.find((u) => String(u?.id) === String(userId));
  if (self) {
    if (self.club_id != null || self.club_name) return PERSPECTIVE.CLUB;
    if (self.agency_id != null || self.agency_name) return PERSPECTIVE.AGENT;
  }
  // agent_agent contexts are always agent-side; club_agent with no self stamp
  // falls back to agent (the safe default — never hides a chat, worst case files
  // an N2 under Agent Req. instead of DM for a rare dual account).
  return PERSPECTIVE.AGENT;
};

export default computeInboxModel;
